import { useCallback, useState } from "react";
import Table, {
  TableActionBar,
  TableCell,
  TableRow,
} from "@amzn/meridian/table";
import {
  ACCESS_DENIED_MESSAGE,
  ADD_FAIL_STATUS,
  ADD_SUCCESS_STATUS,
  DELETE_FAIL_STATUS,
  DELETE_SUCCESS_STATUS,
  DUPLICATE_LOCATION_SIZE_ADMISSIBILITY,
  ITEMS_PER_PAGE,
  PLEASE_WAIT_STATUS,
  LOCATION_LABEL,
  SIZE_LABEL,
  IS_ADMISSIBLE_LABEL,
  SORT_CENTER_LABEL,
  UPDATE_FAIL_STATUS,
  UPDATE_SUCCESS_STATUS,
  USER_ACCESS_KEY,
  DEFAULT_SORT_DIRECTION,
  LOCATION_SIZE_ADMISSIBILITY_HEADERS,
} from "../../constants";
import Button from "@amzn/meridian/button";
import Pagination from "@amzn/meridian/pagination";
import Column from "@amzn/meridian/column";
import LocationSizeAdmissibilityAddForm from "./LocationSizeAdmissibilityAddForm";
import ReadOnlyLocSizeAdmissibilityRow from "./ReadOnlyLocSizeAdmissibilityRow";
import EditableLocSizeAdmissibilityRow from "./EditableLocSizeAdmissibilityRow";
import { IId, ILocationSizeAdmissibility } from "../../common/api/api";
import AmpApi from "../../common/api/AmpApi";
import SubmitModal from "../../components/modal/SubmitModal";
import Loader from "@amzn/meridian/loader";
import filterTokenIcon from "@amzn/meridian-tokens/base/icon/filter";
import Input from "@amzn/meridian/input";
import Toaster from "@amzn/meridian/toaster";
import Alert from "@amzn/meridian/alert";
import {TableSortDirection} from "@amzn/meridian/table/table";

function LocationSizeAdmissibilityTable({
  selectedNode,
  LocationSizeAdmissibilityList,
}) {
  //useState for the list
  const [locationSizeAdmissibilityList, setLocationSizeAdmissibilityList] = useState(LocationSizeAdmissibilityList);
  //useState for filtering table
  const [searchTerm, setSearchTerm] = useState("");
  //For the modal to display message
  const [openModal, setOpenModal] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const onConfirmClose = useCallback(() => setOpenModal(false), []);
  //For the loading sign for api calls
  const [loading, setLoading] = useState(false);
  //For pagination
  const itemsPerPage = ITEMS_PER_PAGE;
  const numberOfPages = Math.ceil(locationSizeAdmissibilityList.length / itemsPerPage);
  const [currentPage, setCurrentPage] = useState(1);
  const firstVisibleIndex = (currentPage - 1) * itemsPerPage;
  const lastVisibleIndex = firstVisibleIndex + itemsPerPage;
  //Pagination
  //For sorting table columns
  const [sortColumn, setSortColumn] = useState(LOCATION_SIZE_ADMISSIBILITY_HEADERS.LOCATION)
  const [sortDirection, setSortDirection] = useState<TableSortDirection>(DEFAULT_SORT_DIRECTION);
  const onSort = useCallback(({sortColumn, sortDirection}) => {
    setSortDirection(sortDirection)
    setSortColumn(sortColumn)
  }, [])
  //For duplicate records error
  let dummyId = 0;
  const [errorMessage, setErrorMessage] = useState("");
  const [toasts, setToasts] = useState([]);
  const onCloseToast = useCallback(
    (id) => setToasts(toasts.filter((t) => t.id !== id)),
    [toasts]
  );
  const onOpenToast = useCallback(
    () => setToasts(toasts.concat({ id: `${++dummyId}` })),
    [toasts]
  );
  //For Add Form
  const [isShown, setIsShown] = useState(false);
  const displayAddForm = () => {
    setIsShown((current) => !current);
  };
  const initialState: ILocationSizeAdmissibility = {
    id: "",
    location: "",
    size: "",
    is_admissible: 0,
  };
  const [addFormData, setAddFormData] = useState(initialState);

  /**
   * This function adds new Inbound Container Eligibility record
   * @param addData The new Inbound Container Eligibility to be added.
   */
  function addFormHandler(addData) {
    let result = locationSizeAdmissibilityList.filter(function (locationSizeAdmissibilityItem) {
      return (
          locationSizeAdmissibilityItem.location == addData.location &&
          locationSizeAdmissibilityItem.size == addData.size &&
          locationSizeAdmissibilityItem.is_admissible == addData.is_admissible
      );
    });
    if (result.length > 0) {
      setErrorMessage(DUPLICATE_LOCATION_SIZE_ADMISSIBILITY);
      onOpenToast();
    } else {
      const addList: ILocationSizeAdmissibility[] = [];
      const finalList = [addData, ...addList];
      setLoading(true);
      //Making api call for add
      (async () => {
        try {
          await AmpApi.updateLocationSizeAdmissibility(
            selectedNode,
            finalList
          );
          setLoading(false);
          setOpenModal(true);
          setResponseMessage(ADD_SUCCESS_STATUS);
          const newFormData = { ...addData };
          setAddFormData(newFormData);
          setLocationSizeAdmissibilityList([addData, ...locationSizeAdmissibilityList]);
        } catch (ex: any) {
          setLoading(false);
          if(localStorage.getItem(USER_ACCESS_KEY)=="Denied"){
            setErrorMessage(ACCESS_DENIED_MESSAGE);
            onOpenToast();
          }
          else {
            console.error(`Error updating ${selectedNode}`, ex);
            setOpenModal(true);
            setResponseMessage(ADD_FAIL_STATUS);
          }
        }
      })();
      //End of the api call for add
    }
  }

  //To edit data
  const [editLocationSizeAdmissibilityId, setEditLocationSizeAdmissibilityId] = useState(null);
  const [editFormData, setEditFormData] = useState(initialState);

  /**
   * This function Updates the Location Size Admissibility
   * @param newFormData Object contains the updated data
   */
  function handleEditFormChange(newFormData) {
    let result = locationSizeAdmissibilityList.filter(function (locationSizeAdmissibilityItem) {
      return (
          locationSizeAdmissibilityItem.location == newFormData.location &&
          locationSizeAdmissibilityItem.size == newFormData.size &&
          locationSizeAdmissibilityItem.is_admissible == newFormData.is_admissible
      );
    });
    if (result.length > 0 && result[0].id != newFormData.id) {
      setErrorMessage(DUPLICATE_LOCATION_SIZE_ADMISSIBILITY);
      onOpenToast();
    } else {
      const updateList: ILocationSizeAdmissibility[] = [];
      const finalList = [newFormData, ...updateList];
      setOpenModal(true);
      setResponseMessage(PLEASE_WAIT_STATUS);
      //Making api call for update
      (async () => {
        try {
          await AmpApi.updateLocationSizeAdmissibility(
            selectedNode,
            finalList
          );
          setResponseMessage(UPDATE_SUCCESS_STATUS);
          const newEditLocationSizeAdmissibilityList = [...locationSizeAdmissibilityList];
          setEditFormData(newFormData);
          const index = newEditLocationSizeAdmissibilityList.findIndex((obj) => {
            return obj.id === newFormData.id;
          });
          setLocationSizeAdmissibilityList(newEditLocationSizeAdmissibilityList);
          setEditLocationSizeAdmissibilityId(null);
        } catch (ex: any) {
          if(localStorage.getItem(USER_ACCESS_KEY)=="Denied"){
            setOpenModal(false);
            setErrorMessage(ACCESS_DENIED_MESSAGE);
            onOpenToast();
          } else {
            console.error(`Error updating ${selectedNode}`, ex);
            setOpenModal(true);
            setResponseMessage(UPDATE_FAIL_STATUS);
          }
        }
      })();
      //End of the api call for update
    }
  }

  const handleEditClick = (locationSizeAdmissibility) => {
    setEditLocationSizeAdmissibilityId(locationSizeAdmissibility.id);
    const formValues: ILocationSizeAdmissibility = {
      id: locationSizeAdmissibility.id,
      location: locationSizeAdmissibility.location,
      size: locationSizeAdmissibility.size,
      is_admissible: locationSizeAdmissibility.is_admissible,
    };
    setEditFormData(formValues);
  };

  /**
   * To handle the cancel button
   */
  const cancelHandler = () => {
    setIsShown((current) => !current);
  };
  const handleCancelClick = () => {
    setEditLocationSizeAdmissibilityId(null);
  };
  /**
   * To handle the delete button
   * @param locationSizeAdmissibility The Location Size Admissibility object to be deleted
   */
  const handleDeleteClick = (locationSizeAdmissibility) => {
    const idsList: IId[] = [];
    const finalIds: IId[] = [locationSizeAdmissibility, ...idsList];
    setOpenModal(true);
    setResponseMessage(PLEASE_WAIT_STATUS);
    //Delete api call starts
    (async () => {
      try {
        await AmpApi.deleteLocationSizeAdmissibility(selectedNode, finalIds);
        setResponseMessage(DELETE_SUCCESS_STATUS);
        const newLocationSizeAdmissibilityList = [...locationSizeAdmissibilityList];
        const index = newLocationSizeAdmissibilityList.findIndex((obj) => {
          return obj.id === locationSizeAdmissibility.id;
        });
        newLocationSizeAdmissibilityList.splice(index, 1);
        setLocationSizeAdmissibilityList(newLocationSizeAdmissibilityList);
      } catch (ex: any) {
        if(localStorage.getItem(USER_ACCESS_KEY)=="Denied"){
          setOpenModal(false);
          setErrorMessage(ACCESS_DENIED_MESSAGE);
          onOpenToast();
        } else {
          console.error(`Error deleting ${locationSizeAdmissibility}`, ex);
          setOpenModal(true);
          setResponseMessage(DELETE_FAIL_STATUS);
        }
      }
    })();
    //Delete api call ends
  };

  //Get list of unique locations, sizes, is_admissibles
  let locations = LocationSizeAdmissibilityList.map(
    (locationSizeAdmissibilityItem) => locationSizeAdmissibilityItem.location
  );
  let uniqueLocations = [...new Set(locations)];
  let sizes = LocationSizeAdmissibilityList.map((a) => a.size);
  let uniqueSizes = [...new Set(sizes)];

  LocationSizeAdmissibilityList.sort((a, b) => {
    if (a[sortColumn] < b[sortColumn]) {
      return sortDirection === "ascending" ? -1 : 1
    }
    if (a[sortColumn] > b[sortColumn]) {
      return sortDirection === "ascending" ? 1 : -1
    }
    return 0
  })

  return (
    <>
      {errorMessage && (
        <Toaster toasts={toasts} onCloseToast={onCloseToast}>
          {(toast) => (
            <Alert toast={true} onClose={toast.onClose} type="error">
              {errorMessage}
            </Alert>
          )}
        </Toaster>
      )}
      <SubmitModal
        open={openModal}
        responseMessage={responseMessage}
        onClose={onConfirmClose}
      />
      {isShown && (
        <Column>
          {loading ? (
            <Loader />
          ) : (
            <LocationSizeAdmissibilityAddForm
              addFormHandler={addFormHandler}
              cancelHandler={cancelHandler}
              uniqueLocations={uniqueLocations}
              uniqueSizes={uniqueSizes}
            />
          )}
        </Column>
      )}
      <Column>
        <Table
            headerRows={1}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={onSort}
            spacing="small"
        >
          <TableActionBar>
            <Button onClick={displayAddForm}>
              {isShown ? "Close" : "Add New"}
            </Button>
            <Input
              key="searchInput"
              type="text"
              value={searchTerm}
              placeholder="Search by Location"
              prefixIconTokens={filterTokenIcon}
              onChange={setSearchTerm}
            />
          </TableActionBar>
          <TableRow>
            <TableCell width={100}>{SORT_CENTER_LABEL}</TableCell>
            <TableCell sortColumn={LOCATION_SIZE_ADMISSIBILITY_HEADERS.LOCATION} width={150}>{LOCATION_LABEL}</TableCell>
            <TableCell sortColumn={LOCATION_SIZE_ADMISSIBILITY_HEADERS.SIZE} width={150}>{SIZE_LABEL}</TableCell>
            <TableCell sortColumn={LOCATION_SIZE_ADMISSIBILITY_HEADERS.IS_ADMISSIBLE} width={200}>{IS_ADMISSIBLE_LABEL}</TableCell>
            <TableCell>Action</TableCell>
          </TableRow>
        </Table>
        {locationSizeAdmissibilityList
          .filter((locationSizeAdmissibility) => {
            if (searchTerm == "") {
              return locationSizeAdmissibility;
            } else if (
                locationSizeAdmissibility.location.toLowerCase().includes(searchTerm.toLowerCase())
            ) {
              return locationSizeAdmissibility;
            }
          })
          .slice(firstVisibleIndex, lastVisibleIndex)
          .map((locationSizeAdmissibility) => (
            <div key={locationSizeAdmissibility.id}>
              {editLocationSizeAdmissibilityId === locationSizeAdmissibility.id ? (
                <EditableLocSizeAdmissibilityRow
                    locSizeAdmissibility={locationSizeAdmissibility}
                    selectedNode={selectedNode}
                    handleEditFormChange={handleEditFormChange}
                    handleCancelClick={handleCancelClick}
                    uniqueLocations={uniqueLocations}
                    uniqueSizes={uniqueSizes}
                />
              ) : (
                <ReadOnlyLocSizeAdmissibilityRow
                    locSizeAdmissibility={locationSizeAdmissibility}
                    selectedNode={selectedNode}
                    handleEditClick={handleEditClick}
                    handleDeleteClick={handleDeleteClick}
                />
              )}
            </div>
          ))}
        <Pagination
          showSkipArrows={true}
          numberOfPages={numberOfPages}
          onChange={setCurrentPage}
          currentPage={currentPage}
        />
      </Column>
    </>
  );
}

export default LocationSizeAdmissibilityTable;
